import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardList, ChevronRight, Calendar, Mail, Phone, CheckCircle2, AlertCircle,
  FileText, Upload, Trash2,
} from "lucide-react";
import { useBiens, biensKeys, useBienContacts } from "@/hooks/useBiens";
import { biensRepository } from "@/repositories/biensRepository";
import { useBienPebPdfUpload } from "@/hooks/useBienPebPdfUpload";
import { SelectField } from "@/components/ui/SelectField";
import { DateField } from "@/components/ui/DateField";
import { TimeField } from "@/components/ui/TimeField";
import { useToast } from "@/components/ui/Toast";
import { CLASSES_PEB } from "@/data/options";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/contexts/RoleContext";
import { usePrestataires } from "@/hooks/usePrestataires";
import { useLocataires } from "@/hooks/useLocataires";
import { getPebStatut, isPebReportComplete, promotePebCycle } from "@/lib/pebCycle";
import { isPrestataireMissionOpen } from "@/v2/lib/bienLifecycle";
import { formatDate, localeFromLng } from "@/v2/lib/formatDate";
import { cn } from "@/lib/utils";
import type { Bien } from "@/types/bien";
import type { BienRow, BienUpdate } from "@/schemas/bien";

// ─────────────────────────────────────────────────────────────────────────────
// Flux guidé prestataire (PEB + ELS), branché sur les vraies machines à états.
//   PEB : Date → Données (certificat) → Clôture     (statut via getPebStatut)
//   ELS : Date → Visite → Transfert élec            (statut via statutEle + transfertElecDate)
// Toutes les sauvegardes passent par update_entity_with_perms (rôle prestataire,
// can_edit déjà ouvert sur les champs concernés).
// ─────────────────────────────────────────────────────────────────────────────

type MType = "peb" | "els";
type MState = "date" | "date_set" | "post_visite" | "donnees" | "transfert" | "fini";

interface Mission {
  key: string;
  bien: BienRow;
  type: MType;
  state: MState;
  date: Date | null;
}

// Clés i18n des libellés d'étapes (résolues au rendu via t()).
const STEP_KEYS: Record<MType, [string, string, string]> = {
  peb: ["v2.missions.stepDate", "v2.missions.stepDonnees", "v2.missions.stepCloture"],
  els: ["v2.missions.stepDate", "v2.missions.stepVisite", "v2.missions.stepTransfert"],
};
const STEP_IDX: Record<MState, number> = {
  date: 0, date_set: 1, post_visite: 1, donnees: 1, transfert: 2, fini: 3,
};
// Clé i18n du badge + tonalité (le libellé est résolu au rendu via t()).
const PILL: Record<MState, { labelKey: string; tone: "danger" | "warning" | "info" | "success" }> = {
  date: { labelKey: "v2.missions.pill.date", tone: "danger" },
  date_set: { labelKey: "v2.missions.pill.dateSet", tone: "info" },
  post_visite: { labelKey: "v2.missions.pill.postVisite", tone: "warning" },
  donnees: { labelKey: "v2.missions.pill.donnees", tone: "warning" },
  transfert: { labelKey: "v2.missions.pill.transfert", tone: "warning" },
  fini: { labelKey: "v2.missions.pill.fini", tone: "success" },
};

function parseDate(d?: string | null, h?: string | null): Date | null {
  if (!d) return null;
  const dt = h ? new Date(`${d}T${h}:00`) : new Date(`${d}T23:59:59`);
  return isNaN(dt.getTime()) ? null : dt;
}
function fmtDate(d: Date | null, lng: string): string {
  return d ? d.toLocaleDateString(localeFromLng(lng)) : "—";
}

/** État PEB dérivé (mockup) à partir des vrais champs. */
function pebState(b: BienRow): MState {
  const s = getPebStatut(b as Parameters<typeof getPebStatut>[0]);
  if (s === "expertise_ok") return "fini";
  if (s === "rapport_en_cours") return "donnees";
  if (!b.pebDatePrevue) return "date";
  return s === "date_passee" ? "post_visite" : "date_set";
}
/** État ELS dérivé : statutEle + edlDatePrevue + transfertElecDate. */
function elsState(b: BienRow): MState {
  const done = b.statutEle === "expertise_ok";
  const transfert = !!(b.transfertElecDate && b.transfertElecDate.trim());
  if (done && transfert) return "fini";
  if (done) return "transfert";
  if (!b.edlDatePrevue) return "date";
  const dt = parseDate(b.edlDatePrevue, b.edlHeure);
  return dt && dt < new Date() ? "post_visite" : "date_set";
}

const PRIORITY: Record<MState, number> = {
  post_visite: 0, donnees: 1, transfert: 1, date_set: 2, date: 3, fini: 4,
};

export function PrestataireMissionsPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { role } = useRole();
  const { data: biens, isLoading } = useBiens();
  const qc = useQueryClient();

  // admin / c_level : vue globale (toutes les missions). Prestataire : les siennes.
  const isAudit = role === "admin" || role === "c_level";
  const pid = profile?.id;

  // Sans onError, un rejet de la RPC (ex. field_permissions.can_edit=false
  // pour ce rôle) était totalement silencieux : le bouton semblait "ne rien
  // faire" au clic, alors qu'il échouait bel et bien (signalé Thibaud
  // 2026-08-20 : "visite effectuée n'est pas cliquable").
  const toast = useToast();
  const saveMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: BienUpdate }) =>
      biensRepository.update(role, id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: biensKeys.all, refetchType: "all" }),
    onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
  });
  const save = (id: string, patch: BienUpdate) => saveMut.mutate({ id, patch });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const missions: Mission[] = useMemo(() => {
    const out: Mission[] = [];
    // Un vrai prestataire assigné = un UUID, pas les valeurs spéciales
    // "amiable"/"autre" (qui signifient "géré hors application").
    const isReal = (v?: string | null) => !!v && v !== "amiable" && v !== "autre";
    for (const b of biens || []) {
      if (!isPrestataireMissionOpen(b)) continue;
      // PEB : tout bien assigné (expertPeb) et éligible, tant que l'expertise
      // n'est pas clôturée. (On ne filtre PAS sur isPebExpiringSoon : l'expert
      // doit voir tous ses biens assignés, même si le certificat est encore
      // valide — cf. liste expert V1 AuditPebPage.)
      // Gate lancement manuel (mig 090) : la mission n'apparaît que si le collab
      // l'a explicitement lancée (pebMissionLanceeAt posé). Cf. edge function launch-mission.
      const pebMine = (isAudit ? isReal(b.expertPeb) : !!pid && b.expertPeb === pid) && !b.expertPebRefuseAt && !!b.pebMissionLanceeAt;
      if (pebMine) {
        const st = pebState(b);
        if (st !== "fini") out.push({ key: `${b.id}:peb`, bien: b, type: "peb", state: st, date: parseDate(b.pebDatePrevue, b.pebHeure) });
      }
      // ELS : seule l'assignation ACTIVE (`prestataireEle` = "Prestataire assigné")
      // fait apparaître le bien. `prestataireEls` ("Prestataire précédent") est une
      // trace pure et ne déclenche RIEN (modèle 2026-06-06).
      const elsMine = isAudit ? isReal(b.prestataireEle) : !!pid && b.prestataireEle === pid;
      // Refus "ELS d'entrée non faite" (prestataireEleRefuseAt) → le bien sort de
      // la liste du prestataire (handback collaborateur). Réinitialisé au cycle
      // suivant via resetElsCycleForLoue.
      // Gate lancement manuel (mig 090) : mission visible seulement si lancée.
      if (elsMine && !b.prestataireEleRefuseAt && !!b.elsMissionLanceeAt) {
        const st = elsState(b);
        if (st !== "fini") out.push({ key: `${b.id}:els`, bien: b, type: "els", state: st, date: parseDate(b.edlDatePrevue, b.edlHeure) });
      }
    }
    return out.sort((a, b) => PRIORITY[a.state] - PRIORITY[b.state] || (a.date?.getTime() ?? Infinity) - (b.date?.getTime() ?? Infinity));
  }, [biens, pid, isAudit]);

  const selected = selectedKey ? missions.find((m) => m.key === selectedKey) ?? null : null;

  // Titre adapté au type de missions du prestataire (un seul rôle `prestataire`,
  // mais en pratique un expert est PEB ou ELS). Vue globale = inchangée.
  const types = new Set(missions.map((m) => m.type));
  const title = isAudit
    ? t("v2.missions.titleAudit")
    : types.size === 1 && types.has("peb")
      ? t("v2.missions.titlePeb")
      : types.size === 1 && types.has("els")
        ? t("v2.missions.titleEls")
        : t("v2.missions.title");

  return (
    <div className="px-3 py-4 md:px-6 md:py-6 mx-auto max-w-2xl lg:max-w-5xl pb-24 md:pb-10">
      <div className="mb-5">
        <h1 className="text-xl md:text-2xl font-semibold text-text-primary flex items-center gap-2">
          <ClipboardList size={22} className="text-accent" />
          {title}
        </h1>
        <p className="text-xs md:text-sm text-text-muted mt-1">
          {t("v2.missions.count", { count: missions.length })}
          {!isAudit && profile?.first_name ? ` · ${profile.first_name}` : ""}
        </p>
      </div>

      {isLoading ? (
        <div className="text-center text-text-muted py-12">{t("common.loading")}</div>
      ) : missions.length === 0 ? (
        <div className="text-center text-text-muted/70 py-16">
          <CheckCircle2 size={28} className="mx-auto mb-2 opacity-40" />
          <p>{t("v2.missions.empty")}</p>
          <p className="text-xs text-text-muted/60 mt-3 max-w-md mx-auto leading-relaxed">{t("v2.missions.emptyHint")}</p>
        </div>
      ) : (
        <>
          {/* Mobile : cartes repliables */}
          <div className="lg:hidden flex flex-col gap-3">
            {missions.map((m) => (
              <MissionRow key={m.key} mission={m} save={save} saving={saveMut.isPending} />
            ))}
          </div>
          {/* Desktop : tableau + panneau latéral */}
          <div className="hidden lg:block bg-surface border border-border rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-2 text-[10px] uppercase tracking-wide text-text-muted">
                  {[t("common.type"), t("v2.missions.col.reference"), t("common.address"), t("common.city"), t("v2.missions.col.avancement"), t("v2.missions.col.rdv"), t("common.status"), ""].map((h, i) => (
                    <th key={i} className={cn("text-left font-semibold px-3 py-2.5 border-b border-border", i === 7 && "text-right")}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {missions.map((m) => (
                  <MissionTableRow key={m.key} mission={m} selected={m.key === selectedKey} onClick={() => setSelectedKey(m.key)} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selected && (
        <MissionDrawer mission={selected} save={save} saving={saveMut.isPending} onClose={() => setSelectedKey(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const TONE: Record<string, string> = {
  danger: "bg-danger/10 text-danger border-danger/25",
  warning: "bg-warning/10 text-warning border-warning/25",
  info: "bg-accent/10 text-accent border-accent/30",
  success: "bg-success/10 text-success border-success/25",
};

function MissionRow({ mission, save, saving }: { mission: Mission; save: (id: string, p: BienUpdate) => void; saving: boolean }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const { bien: b, type, state } = mission;
  const pill = PILL[state];
  const typeLabel = type === "peb" ? "PEB" : "ELS";

  return (
    <div className={cn("bg-surface border rounded-xl shadow-sm", open ? "border-accent/40" : "border-border")}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left p-4 cursor-pointer">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-surface-2 text-text-secondary">{typeLabel}</span>
            <span className="font-mono font-semibold text-sm text-text-primary truncate">{b.referenceInterne || b.reference || "—"}</span>
          </span>
          <span className={cn("text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border whitespace-nowrap", TONE[pill.tone])}>{t(pill.labelKey)}</span>
        </div>
        <div className="flex items-baseline justify-between gap-3 mt-2">
          <span className="font-medium text-sm text-text-primary truncate">{b.adresse || "—"}</span>
          <span className="text-xs text-text-muted flex items-center gap-1.5 flex-shrink-0">
            {b.ville || "—"}
            <ChevronRight size={13} className={cn("transition-transform", open && "rotate-90")} />
          </span>
        </div>
        {(state === "date_set" || state === "post_visite") && mission.date && (
          <div className={cn("mt-1.5 text-xs font-semibold flex items-center gap-1.5", state === "post_visite" ? "text-warning" : "text-accent")}>
            <Calendar size={12} /> {t("v2.missions.rdv", { date: fmtDate(mission.date, i18n.language) })}{(type === "peb" ? b.pebHeure : b.edlHeure) ? ` · ${type === "peb" ? b.pebHeure : b.edlHeure}` : ""}
          </div>
        )}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <MissionFlow mission={mission} save={save} saving={saving} />
        </div>
      )}
    </div>
  );
}

function Stepper({ type, state }: { type: MType; state: MState }) {
  const { t } = useTranslation();
  const idx = STEP_IDX[state];
  const labels = STEP_KEYS[type].map((k) => t(k));
  return (
    <div className="flex items-center my-3">
      {labels.map((lbl, i) => (
        <div key={lbl} className="contents">
          <div className="flex flex-col items-center w-14 flex-shrink-0">
            <span className={cn("w-6 h-6 rounded-full border-2 flex items-center justify-center text-[11px] font-bold",
              i < idx ? "bg-success border-success text-white" : i === idx ? "bg-accent/10 border-accent text-accent" : "border-border text-text-muted bg-surface")}>
              {i < idx ? "✓" : i + 1}
            </span>
            <span className={cn("text-[9px] uppercase tracking-wide mt-1", i === idx ? "text-text-secondary font-semibold" : "text-text-muted")}>{lbl}</span>
          </div>
          {i < labels.length - 1 && <span className={cn("flex-1 h-0.5 -mt-4", i < idx ? "bg-success" : "bg-border")} />}
        </div>
      ))}
    </div>
  );
}

const btn = "w-full rounded-lg py-3 text-sm font-semibold cursor-pointer transition-colors";
const btnPrimary = cn(btn, "bg-accent text-white hover:bg-accent-hover");
// Même accent que le bouton "Lancer" une mission ELS/PEB du Pipeline
// (PipelineActionsPopover), pas `bg-success` (signalé Thibaud 2026-08-20 :
// "c'est pas le bon vert... le même vert que lancer une mission peb ou els").
const btnYes = cn(btn, "bg-accent hover:bg-accent-hover text-white");
const btnNo = cn(btn, "bg-surface-2 text-text-secondary");
const btnGhost = cn(btn, "bg-surface-2 text-text-secondary text-[13px] py-2.5 font-medium");

/** Date de validité par défaut d'un nouveau certificat PEB : aujourd'hui + 10 ans
 *  (durée de validité standard, décision user 2026-08-20). Reste un pré-remplissage
 *  modifiable, pas une contrainte — l'expert peut toujours corriger la date. */
function defaultPebFinLe(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 10);
  return d.toISOString().slice(0, 10);
}

function MissionFlow({ mission, save, saving }: { mission: Mission; save: (id: string, p: BienUpdate) => void; saving: boolean }) {
  const { t, i18n } = useTranslation();
  const { bien: b, type, state } = mission;
  const { profile } = useAuth();
  // Gate ELS : confirmer l'état des lieux d'ENTRÉE avant de planifier la sortie.
  // Non persisté (1-clic) ; "Non" enregistre le refus → handback collaborateur.
  const [gateOk, setGateOk] = useState(false);
  const [editingDate, setEditingDate] = useState(state === "date");
  const [d, setD] = useState((type === "peb" ? b.pebDatePrevue : b.edlDatePrevue) || "");
  const [h, setH] = useState((type === "peb" ? b.pebHeure : b.edlHeure) || "");
  // PEB nouveau certificat (staging, saisi par l'expert) — modèle brouillon V1.
  // La clôture promeut ces champs vers les champs live via promotePebCycle().
  const [classe, setClasse] = useState(b.nouvClassePeb || "");
  const [num, setNum] = useState(b.nouvCertificatPeb || "");
  const [conso, setConso] = useState(b.nouvConsoKwh || "");
  const [emis, setEmis] = useState(b.nouvEmissionCo2 || "");
  const [finPeb, setFinPeb] = useState(b.nouvPebFinLe || defaultPebFinLe());
  const [pdfUrl, setPdfUrl] = useState(b.nouvPebCertificatPdf || "");
  const [pebComment, setPebComment] = useState(b.pebCommentaire || "");
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const pdfUpload = useBienPebPdfUpload(b.id);
  // ELS transfert
  const [transfertD, setTransfertD] = useState(b.transfertElecDate || "");
  const [elsComment, setElsComment] = useState(b.edlCommentaire || "");

  const id = b.id;
  const dateField = type === "peb" ? "pebDatePrevue" : "edlDatePrevue";
  const heureField = type === "peb" ? "pebHeure" : "edlHeure";

  // Gate clôture : les 5 champs nouv* du certificat (isPebReportComplete V1)
  // PLUS le PDF — pour garantir que le bien promu satisfasse isPebDone côté V1
  // (liste audit "terminés"). Sans cela, l'admin verrait le bien comme non fini.
  const stagingComplete =
    isPebReportComplete({
      nouvClassePeb: classe,
      nouvCertificatPeb: num,
      nouvPebFinLe: finPeb,
      nouvConsoKwh: conso,
      nouvEmissionCo2: emis,
    }) && !!pdfUrl.trim();

  // Upload PDF nouveau certificat (staging). Remplace l'ancien (best-effort).
  const handlePdfPick = async (file: File | null) => {
    if (!file) return;
    setPdfError(null);
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setPdfError(t("v2.missions.pebPdfOnly"));
      return;
    }
    try {
      const previousPath = pdfUpload.pathFromUrl(pdfUrl);
      const { url } = await pdfUpload.upload(file, previousPath);
      setPdfUrl(url);
    } catch (e) {
      setPdfError((e as Error).message);
    }
  };
  const handlePdfDelete = async () => {
    if (!pdfUrl) return;
    setPdfError(null);
    const path = pdfUpload.pathFromUrl(pdfUrl);
    if (path) {
      try { await pdfUpload.remove(path); } catch {/* best-effort */}
    }
    setPdfUrl("");
  };

  // Clôture PEB : promotion staging → live via promotePebCycle (modèle V1).
  const handlePebCloture = () => {
    if (!stagingComplete) return;
    const staged: Bien = {
      ...(b as unknown as Bien),
      pebCommentaire: pebComment,
      nouvClassePeb: classe,
      nouvCertificatPeb: num,
      nouvPebFinLe: finPeb,
      nouvConsoKwh: conso,
      nouvEmissionCo2: emis,
      nouvPebCertificatPdf: pdfUrl,
    };
    save(id, { ...promotePebCycle(staged), pebCommentaire: pebComment } as BienUpdate);
  };

  return (
    <div className="border-t border-border pt-3">
      <Stepper type={type} state={state} />
      <ContextBlock mission={mission} />

      {/* GATE ELS : état des lieux d'entrée fait ? (avant de planifier la sortie) */}
      {type === "els" && state === "date" && !gateOk ? (
        <div className="mt-1">
          <p className="text-sm text-text-secondary mb-2"><Trans i18nKey="v2.missions.elsGateQuestion" components={{ 1: <b /> }} /></p>
          <div className="flex gap-2">
            <button disabled={saving} className={btnYes} onClick={() => setGateOk(true)}>{t("common.yes")}</button>
            <button disabled={saving} className={btnNo} onClick={() => save(b.id, { prestataireEleRefusePar: profile?.id ?? b.prestataireEle ?? "", prestataireEleRefuseAt: new Date().toISOString(), prestataireEls: b.prestataireEle ?? "", prestataireEle: "" })}>{t("common.no")}</button>
          </div>
          <p className="text-[11px] text-text-muted mt-2">{t("v2.missions.elsGateNoHint")}</p>
        </div>
      ) : /* DATE : à planifier ou modification du RDV */
      (state === "date" || editingDate) ? (
        <div className="mt-1">
          <p className="text-sm text-text-secondary mb-2">
            {type === "peb" ? t("v2.missions.fixDatePeb") : t("v2.missions.fixDateEls")}
          </p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <DateField label={t("v2.missions.labelDate")} value={d} onChange={setD} />
            <TimeField label={t("v2.missions.labelHeure")} value={h} onChange={setH} />
          </div>
          <button disabled={!d || saving} className={cn(btnPrimary, (!d || saving) && "opacity-50")}
            onClick={() => { save(id, { [dateField]: d, [heureField]: h } as BienUpdate); setEditingDate(false); }}>
            {t("v2.missions.confirmRdv")}
          </button>
          {state !== "date" && <button className={btnGhost} onClick={() => setEditingDate(false)}>{t("common.cancel")}</button>}
          {/* Refus PEB (migration 068) : l'expert rend la main → handback collab. */}
          {type === "peb" && state === "date" && (
            <button disabled={saving} className={btnGhost}
              onClick={() => save(b.id, { expertPebRefusePar: profile?.id ?? b.expertPeb ?? "", expertPebRefuseAt: new Date().toISOString() })}>
              {t("v2.missions.pebRefuse")}
            </button>
          )}
        </div>
      ) : state === "date_set" ? (
        <div className="mt-1">
          <div className="text-sm text-text-secondary flex items-center gap-2">🗓 {t("v2.missions.rdvSet")} <b className="text-text-primary">{fmtDate(mission.date, i18n.language)}{h ? ` · ${h}` : ""}</b></div>
          <p className="text-[11px] text-text-muted mt-1.5">{t("v2.missions.rdvEmailHint")}</p>
          <button className={btnGhost} onClick={() => setEditingDate(true)}>{t("v2.missions.editRdv")}</button>
        </div>
      ) : state === "post_visite" ? (
        <div className="mt-1">
          <p className="text-sm text-text-secondary mb-2">
            <Trans i18nKey={type === "peb" ? "v2.missions.rdvPassedPeb" : "v2.missions.rdvPassedEls"} values={{ date: fmtDate(mission.date, i18n.language) }} components={{ 1: <b />, 3: <b /> }} />
          </p>
          <div className="flex gap-2">
            <button disabled={saving} className={btnYes} onClick={() => save(id, (type === "peb" ? { statutPeb: "rapport_en_cours" } : { statutEle: "expertise_ok" }) as BienUpdate)}>{t("v2.missions.visitDone")}</button>
            <button className={btnNo} onClick={() => setEditingDate(true)}>{t("v2.missions.reschedule")}</button>
          </div>
        </div>
      ) : state === "donnees" ? (
        // PEB : saisie du NOUVEAU certificat (staging) + clôture (promotePebCycle).
        <div className="mt-1">
          <div className="flex items-center justify-center gap-2 text-success font-semibold text-sm mb-2"><CheckCircle2 size={16} /> {t("v2.missions.visitDoneBadge")}</div>

          {/* Certificat actuel (live) — lecture seule, pour comparaison (miroir V1). */}
          {(b.classePeb || b.pebFinLe || b.certificatPeb || b.pebCertificatPdf) && (
            <div className="bg-surface-2 border border-border rounded-lg px-3 py-2 mb-3 text-[12px]">
              <div className="text-[9px] uppercase tracking-wide text-text-muted mb-1">{t("v2.missions.pebCurrentCert")}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-text-secondary">
                <span>{t("v2.missions.fieldClassePeb")} : <b className="text-text-primary">{b.classePeb || "—"}</b></span>
                <span>{t("v2.missions.fieldValableJusqu")} : <b className="text-text-primary">{b.pebFinLe ? fmtDate(parseDate(b.pebFinLe), i18n.language) : "—"}</b></span>
                {b.certificatPeb && <span>{t("v2.missions.fieldNumCertificat")} : <b className="text-text-primary">{b.certificatPeb}</b></span>}
              </div>
              {b.pebCertificatPdf && (
                <a href={b.pebCertificatPdf} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-accent font-medium mt-1"><FileText size={12} /> {t("v2.missions.pebOpenPdf")}</a>
              )}
            </div>
          )}

          <p className="text-sm text-text-secondary mb-2"><b>{t("v2.missions.pebNewCert")}</b></p>
          <div className="mb-2">
            <SelectField label={t("v2.missions.fieldClassePeb")} value={classe} onChange={setClasse} options={CLASSES_PEB} />
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Field label={t("v2.missions.fieldNumCertificat")}><input value={num} onChange={(e) => setNum(e.target.value)} className={inputCls} /></Field>
            <Field label={t("v2.missions.fieldConso")}><input inputMode="numeric" value={conso} onChange={(e) => setConso(e.target.value)} className={inputCls} /></Field>
            <Field label={t("v2.missions.fieldEmission")}><input inputMode="numeric" value={emis} onChange={(e) => setEmis(e.target.value)} className={inputCls} /></Field>
            <DateField label={t("v2.missions.fieldValableJusqu")} value={finPeb} onChange={setFinPeb} />
          </div>

          {/* Upload PDF nouveau certificat (staging → nouvPebCertificatPdf). */}
          <div className="mb-3">
            <span className="block text-[10px] uppercase tracking-wide text-text-muted mb-1">{t("v2.missions.pebCertificatPdf")}</span>
            {pdfUrl ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-surface border border-border rounded-md">
                <FileText size={14} className="text-accent flex-shrink-0" />
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline truncate flex-1">{t("v2.missions.pebOpenPdf")}</a>
                <button type="button" onClick={() => void handlePdfDelete()} className="p-1 text-text-muted hover:text-danger transition-colors cursor-pointer" aria-label={t("v2.missions.pebDeletePdf")} title={t("v2.missions.pebDeletePdf")}>
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => pdfInputRef.current?.click()} disabled={pdfUpload.uploading}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm bg-surface text-text-secondary hover:text-accent border border-dashed border-border hover:border-accent transition-colors cursor-pointer">
                <Upload size={14} /> {pdfUpload.uploading ? t("common.saving") : t("v2.missions.pebUploadPdf")}
              </button>
            )}
            <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" className="hidden"
              onChange={(e) => { void handlePdfPick(e.target.files?.[0] ?? null); e.target.value = ""; }} />
            {pdfError && <span className="block text-xs text-danger mt-1">{pdfError}</span>}
          </div>

          <Field label={t("v2.missions.pebCommentaire")}>
            <textarea value={pebComment} onChange={(e) => setPebComment(e.target.value)} rows={2} className={cn(inputCls, "resize-y")} />
          </Field>

          <button disabled={!stagingComplete || saving} className={cn(btnPrimary, "mt-3", (!stagingComplete || saving) && "opacity-50")}
            onClick={handlePebCloture}>
            {t("v2.missions.savePebCloture")}
          </button>
          {!stagingComplete && <p className="text-[11px] text-text-muted mt-1.5">{t("v2.missions.pebClotureHint")}</p>}
        </div>
      ) : state === "transfert" ? (
        // ELS : transfert compteur électrique + commentaire état des lieux
        <div className="mt-1">
          <div className="flex items-center justify-center gap-2 text-success font-semibold text-sm mb-2"><CheckCircle2 size={16} /> {t("v2.missions.elsDoneBadge")}</div>
          <p className="text-sm text-text-secondary mb-2"><Trans i18nKey="v2.missions.transfertQuestion" components={{ 1: <b /> }} /></p>
          <DateField label={t("v2.missions.fieldDateTransfert")} value={transfertD} onChange={setTransfertD} />
          <div className="mt-2">
            <Field label={t("v2.missions.elsCommentaire")}>
              <textarea value={elsComment} onChange={(e) => setElsComment(e.target.value)} rows={2} className={cn(inputCls, "resize-y")} />
            </Field>
          </div>
          <button disabled={!transfertD || saving} className={cn(btnPrimary, "mt-2", (!transfertD || saving) && "opacity-50")}
            onClick={() => save(id, { transfertElecDate: transfertD, edlCommentaire: elsComment } as BienUpdate)}>
            {t("v2.missions.transfertDone")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-border rounded-md bg-surface text-sm text-text-primary outline-none";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wide text-text-muted mb-1">{label}</span>
      {children}
    </label>
  );
}

/** Bloc contexte : fin de contrat, date de l'autre expertise, expert lié, locataire actuel. */
function ContextBlock({ mission }: { mission: Mission }) {
  const { t, i18n } = useTranslation();
  const { bien: b, type } = mission;
  const { data: prestataires } = usePrestataires();
  // Contact scopé à CE bien (collaborateurSrs/expertPeb/prestataireEle),
  // jamais toute la table profils — voir migration 126.
  const { data: bienContacts } = useBienContacts(b.id);
  const { data: locataires } = useLocataires(b.id);

  const pmap = useMemo(() => {
    const m = new Map<string, { nom: string; email?: string; tel?: string }>();
    (prestataires || []).forEach((p) => { if (p.id && (p.nom || "").trim()) m.set(p.id, { nom: (p.nom || "").trim(), email: p.email, tel: p.telephone }); });
    (bienContacts || []).forEach((p) => {
      const nom = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
      if (p.id && nom && !m.has(p.id)) m.set(p.id, { nom, email: p.email ?? undefined, tel: p.telephone ?? undefined });
    });
    return m;
  }, [prestataires, bienContacts]);
  // Le contact pertinent pour une mission de SORTIE (ELS/PEB) est le locataire
  // SORTANT = dernier type='ancien' (le repo trie les anciens par dateFin desc).
  // Au moment où la mission est visible, l'actuel est soit un slot vide, soit
  // déjà le locataire ENTRANT (relocation anticipée) — jamais le sortant.
  // Fallback sur l'actuel si aucun ancien (bien jamais tourné).
  const sortant = (locataires || []).find((l) => l.type === "ancien")
    ?? (locataires || []).find((l) => l.type === "actuel");

  // PEB regarde l'ELS (assigné = prestataireEle + edlDatePrevue) ; ELS regarde le PEB (expertPeb + pebDatePrevue).
  const otherId = type === "peb" ? b.prestataireEle : b.expertPeb;
  const special = otherId === "amiable" ? t("v2.pipeline.expert.amiable") : otherId === "autre" ? t("v2.pipeline.expert.autre") : null;
  const other = otherId && !special ? pmap.get(otherId) : special ? { nom: special } : undefined;
  const collab = b.collaborateurSrs ? pmap.get(b.collaborateurSrs) : undefined;
  const otherLabel = type === "peb" ? t("v2.missions.ctxOtherExpertEls") : t("v2.missions.ctxOtherExpertPeb");
  const otherDate = type === "peb" ? b.edlDatePrevue : b.pebDatePrevue;
  const otherHeure = type === "peb" ? b.edlHeure : b.pebHeure;
  const otherDateLabel = type === "peb" ? t("v2.missions.ctxElsDateLabel") : t("v2.missions.ctxPebDateLabel");

  return (
    <div className="text-[13px] text-text-secondary mb-3">
      <p className="mb-2">
        <Trans i18nKey="v2.missions.ctxContractEnd" values={{ date: b.finDeContrat ? formatDate(b.finDeContrat, i18n.language) : "—" }} components={{ 1: <b className="text-text-primary" /> }} />
      </p>
      {otherDate ? (
        <div className="bg-accent/10 text-accent rounded-lg px-3 py-2 mb-2 font-semibold flex items-center gap-2">
          <Calendar size={13} /> {t("v2.missions.ctxOtherDate", { label: otherDateLabel, date: formatDate(otherDate, i18n.language) })}{otherHeure ? ` · ${otherHeure}` : ""}
        </div>
      ) : (
        <div className="text-[11px] text-text-muted mb-2 flex items-center gap-1"><AlertCircle size={11} /> {t("v2.missions.ctxOtherDateNotSet", { label: otherDateLabel })}</div>
      )}
      {(other?.nom || otherId) && (
        <ContactCard label={otherLabel} nom={other?.nom || "—"} email={other?.email} tel={other?.tel} />
      )}
      {(collab?.nom || b.collaborateurSrs) && (
        <ContactCard label={t("v2.missions.ctxResponsable")} nom={collab?.nom || "—"} email={collab?.email} tel={collab?.tel} />
      )}
      <ContactCard label={t("v2.missions.ctxLocataireSortant")} nom={sortant?.nom || "—"} email={sortant?.email} tel={sortant?.telephone} />
    </div>
  );
}

// ── Desktop : tableau + drawer ───────────────────────────────────────────────

function MiniStepper({ type, state }: { type: MType; state: MState }) {
  const idx = STEP_IDX[state];
  return (
    <div className="flex items-center">
      {STEP_KEYS[type].map((_, i) => (
        <div key={i} className="contents">
          <span className={cn("w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center text-[9px] font-bold flex-shrink-0",
            i < idx ? "bg-success border-success text-white" : i === idx ? "bg-accent/10 border-accent text-accent" : "border-border text-text-muted bg-surface")}>
            {i < idx ? "✓" : i + 1}
          </span>
          {i < 2 && <span className={cn("w-5 h-0.5", i < idx ? "bg-success" : "bg-border")} />}
        </div>
      ))}
    </div>
  );
}

function MissionTableRow({ mission, selected, onClick }: { mission: Mission; selected: boolean; onClick: () => void }) {
  const { t, i18n } = useTranslation();
  const { bien: b, type, state } = mission;
  const pill = PILL[state];
  const heure = type === "peb" ? b.pebHeure : b.edlHeure;
  return (
    <tr onClick={onClick} className={cn("border-b border-border last:border-0 cursor-pointer transition-colors", selected ? "bg-accent/10" : "hover:bg-accent/5")}>
      <td className="px-3 py-2.5"><span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-surface-2 text-text-secondary">{type === "peb" ? "PEB" : "ELS"}</span></td>
      <td className="px-3 py-2.5 font-mono font-semibold text-text-primary">{b.referenceInterne || b.reference || "—"}</td>
      <td className="px-3 py-2.5 font-medium text-text-primary">{b.adresse || "—"}</td>
      <td className="px-3 py-2.5 text-text-muted">{b.ville || "—"}</td>
      <td className="px-3 py-2.5"><MiniStepper type={type} state={state} /></td>
      <td className="px-3 py-2.5 text-text-muted whitespace-nowrap">{mission.date ? `${fmtDate(mission.date, i18n.language)}${heure ? ` · ${heure}` : ""}` : "—"}</td>
      <td className="px-3 py-2.5"><span className={cn("text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border whitespace-nowrap", TONE[pill.tone])}>{t(pill.labelKey)}</span></td>
      <td className="px-3 py-2.5 text-right"><ChevronRight size={15} className="text-text-muted inline" /></td>
    </tr>
  );
}

function MissionDrawer({ mission, save, saving, onClose }: { mission: Mission; save: (id: string, p: BienUpdate) => void; saving: boolean; onClose: () => void }) {
  const { bien: b, type } = mission;
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className="absolute top-0 right-0 h-full w-[440px] max-w-[92vw] bg-surface shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-surface-2 text-text-secondary">{type === "peb" ? "PEB" : "ELS"}</span>
              <span className="font-mono font-semibold text-text-primary">{b.referenceInterne || b.reference || "—"}</span>
            </div>
            <div className="font-medium text-text-primary mt-1.5">{b.adresse || "—"}</div>
            <div className="text-xs text-text-muted">{b.ville || "—"}</div>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary text-xl leading-none cursor-pointer">×</button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">
          <MissionFlow mission={mission} save={save} saving={saving} />
        </div>
      </aside>
    </div>
  );
}

function ContactCard({ label, nom, email, tel }: { label: string; nom: string; email?: string; tel?: string }) {
  return (
    <div className="bg-surface-2 border border-border rounded-lg px-3 py-2 mb-2">
      <div className="text-[9px] uppercase tracking-wide text-text-muted mb-1">{label}</div>
      <div className="text-text-primary font-semibold">{nom}</div>
      {email && <a href={`mailto:${email}`} className="flex items-center gap-1.5 text-accent font-medium mt-0.5"><Mail size={12} /> {email}</a>}
      {tel && <a href={`tel:${tel.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-accent font-medium mt-0.5"><Phone size={12} /> {tel}</a>}
    </div>
  );
}
